import { useEffect, useMemo, useRef, useState } from "react";
import phoneCountries from "../../../catalogs/phoneCountries";

const normalizePhoneCode = (code) => {
  const c = String(code ?? "").trim();
  if (!c) return "";
  return c.startsWith("+") ? c : `+${c}`;
};

function CustomerFormModal({ isOpen, onClose, customerData, onSubmit }) {
  const isEdit = !!customerData;

  const initialForm = useMemo(
    () => ({
      nombre: customerData?.nombre || "",
      apellido: customerData?.apellido || "",
      codigo_telefono: customerData?.codigo_telefono || "",
      telefono: customerData?.telefono || "",
      correo: customerData?.correo || "",
      tipo_cliente: customerData?.tipo_cliente || "individual",
      empresa: customerData?.empresa || "",
      razon_social: customerData?.razon_social || "",
      vat_tax_id: customerData?.vat_tax_id || "",
      correo_facturacion: customerData?.correo_facturacion || "",
    }),
    [customerData]
  );

  const [form, setForm] = useState(initialForm);
  const [alert, setAlert] = useState(null);
  const alertTimerRef = useRef(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setForm(initialForm);
    setAlert(null);
    setSaving(false);

    return () => {
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
  }, [initialForm, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const showAlert = (type, message) => {
    setAlert({ type, message: String(message || "") });

    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 4000);
  };

  const validatePhoneCode = (code) => {
    const c = String(code ?? "").trim();
    if (!c) return true;
    return /^\+?\d{1,6}$/.test(c);
  };

  const validateEmail = (email) => {
    if (!email) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const updateField = (e) => {
    const { name, value } = e.target;

    if (name === "codigo_telefono") {
      setForm((prev) => ({ ...prev, [name]: normalizePhoneCode(value) }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const countryOptions = useMemo(() => {
    const arr = Array.isArray(phoneCountries) ? phoneCountries : [];
    return arr
      .map((c) => {
        const dial = c?.code;
        if (!dial) return null;

        const value = normalizePhoneCode(dial);
        const label = `${c?.flag ? `${c.flag} ` : ""}${c?.country || ""}${
          c?.iso ? ` (${c.iso})` : ""
        } ${value}`.trim();

        return { value, label };
      })
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  const selectedCountry = useMemo(() => {
    const code = normalizePhoneCode(form.codigo_telefono);
    if (!code) return null;
    return (Array.isArray(phoneCountries) ? phoneCountries : []).find(
      (c) => normalizePhoneCode(c?.code) === code
    );
  }, [form.codigo_telefono]);

  const phoneMin = selectedCountry?.minLength ?? undefined;
  const phoneMax = selectedCountry?.maxLength ?? undefined;

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;

    setAlert(null);

    const nombreN = String(form.nombre || "").trim();
    const apellidoN = String(form.apellido || "").trim();
    const correoN = String(form.correo || "").trim();
    const telefonoN = String(form.telefono || "").trim();
    const codigoTelN = normalizePhoneCode(form.codigo_telefono);

    if (!nombreN) return showAlert("error", "Name is required.");
    if (!apellidoN) return showAlert("error", "Lastname is required.");
    if (correoN && !validateEmail(correoN)) return showAlert("error", "Email is not valid.");

    if (codigoTelN && !validatePhoneCode(codigoTelN)) {
      return showAlert("error", "Country code is not valid. Example: +502");
    }

    if (telefonoN && phoneMin && telefonoN.replace(/\D/g, "").length < phoneMin) {
      return showAlert("error", `Phone must have at least ${phoneMin} digits for this country.`);
    }
    if (telefonoN && phoneMax && telefonoN.replace(/\D/g, "").length > phoneMax) {
      return showAlert("error", `Phone must have at most ${phoneMax} digits for this country.`);
    }

    const esComercial = form.tipo_cliente === "comercial";
    const razonSocialN = String(form.razon_social || "").trim();
    const correoFactN = String(form.correo_facturacion || "").trim();
    const vatN = String(form.vat_tax_id || "").trim();

    /* El nombre legal es el que va impreso en la factura: sin el, la
       cuenta queda marcada como empresa y no se le puede facturar. */
    if (esComercial && !razonSocialN) {
      return showAlert("error", "Legal company name is required for a company account.");
    }

    if (esComercial && correoFactN && !validateEmail(correoFactN)) {
      return showAlert("error", "Billing email is not valid.");
    }

    if (esComercial && vatN && !/^[A-Za-z0-9.\-/ ]+$/.test(vatN)) {
      return showAlert("error", "VAT/Tax ID can only have letters, numbers, dots, slashes and dashes.");
    }

    const payload = {
      nombre: nombreN,
      apellido: apellidoN,
      correo: correoN,
      telefono: telefonoN,
      codigo_telefono: codigoTelN || "",
      tipo_cliente: form.tipo_cliente,
      /* Se mandan siempre los cuatro. El backend los descarta cuando la
         cuenta es individual, asi que no hay dos formas del mismo pedido
         segun el tipo. */
      empresa: String(form.empresa || "").trim(),
      razon_social: razonSocialN,
      vat_tax_id: vatN,
      correo_facturacion: correoFactN,
    };

    setSaving(true);

    try {
      await onSubmit(payload);
      onClose?.();
    } catch (error) {
      console.error("Customer save failed:", error);
      showAlert("error", error?.message || "Could not save customer.");
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={ui.overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose?.();
      }}
    >
      <div style={ui.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div style={ui.header}>
          <h2 style={ui.title}>{isEdit ? "Edit Customer" : "Create Customer"}</h2>
          <button style={ui.closeBtn} onClick={onClose} disabled={saving} title="Close">
            ✕
          </button>
        </div>

        {alert && (
          <div
            style={{
              ...ui.alert,
              ...(alert.type === "error" ? ui.alertError : ui.alertInfo),
            }}
          >
            <span>{alert.message}</span>
            <button style={ui.alertX} onClick={() => setAlert(null)} disabled={saving}>
              ✕
            </button>
          </div>
        )}

        <form onSubmit={submit} style={ui.form}>
          {/* Lo primero que se elige, porque decide que mas hay que
              llenar. Antes no existia: todo cliente creado desde el admin
              nacia individual y no habia forma de cambiarlo. */}
          <div style={{ ...ui.inputGroup, ...ui.ancho }}>
            <label style={ui.label}>Account type</label>

            <div style={ui.tipoRow}>
              {[
                {
                  valor: "individual",
                  titulo: "Individual",
                  ayuda: "Buys for themselves",
                },
                {
                  valor: "comercial",
                  titulo: "Company",
                  ayuda: "Buys for a business",
                },
              ].map((op) => {
                const elegido = form.tipo_cliente === op.valor;

                return (
                  <label
                    key={op.valor}
                    style={{
                      ...ui.tipoCard,
                      ...(elegido ? ui.tipoCardOn : null),
                    }}
                  >
                    <input
                      type="radio"
                      name="tipo_cliente"
                      value={op.valor}
                      checked={elegido}
                      onChange={updateField}
                      disabled={saving}
                      style={ui.tipoRadio}
                    />
                    <span>
                      <span style={ui.tipoTitulo}>{op.titulo}</span>
                      <span style={ui.tipoAyuda}>{op.ayuda}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <Input
            label="Name"
            name="nombre"
            value={form.nombre}
            onChange={updateField}
            required
            disabled={saving}
            autoFocus
          />

          <Input
            label="Lastname"
            name="apellido"
            value={form.apellido}
            onChange={updateField}
            required
            disabled={saving}
          />

          {/* Cada uno en su celda de la grilla: dentro de una sub-fila
              quedaban los dos en media pantalla y el nombre del pais no
              entraba. */}
          <div style={ui.inputGroup}>
            <label style={ui.label}>Country Code</label>

            <select
              name="codigo_telefono"
              value={form.codigo_telefono ? normalizePhoneCode(form.codigo_telefono) : ""}
              onChange={updateField}
              disabled={saving}
              style={ui.select}
            >
              <option value="">Select code...</option>
              {countryOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div style={ui.inputGroup}>
            <Input
              label="Phone"
              name="telefono"
              value={form.telefono}
              onChange={updateField}
              disabled={saving}
              inputMode="tel"
              maxLength={phoneMax}
              placeholder={phoneMax ? `Up to ${phoneMax} digits` : ""}
            />

            {selectedCountry && (phoneMin || phoneMax) && (
              <div style={ui.help}>
                {selectedCountry.flag} {selectedCountry.country}:{" "}
                {phoneMin ? `min ${phoneMin}` : ""}{phoneMin && phoneMax ? " / " : ""}
                {phoneMax ? `max ${phoneMax}` : ""} digits
              </div>
            )}
          </div>

          <div style={ui.ancho}>
            <Input
              label="Email"
              type="email"
              name="correo"
              value={form.correo}
              onChange={updateField}
              disabled={saving}
            />
          </div>

          {form.tipo_cliente === "comercial" && (
            <>
              <div style={{ ...ui.seccion, ...ui.ancho }}>Company details</div>

              <div style={ui.ancho}>
                <Input
                  label="Legal company name *"
                  name="razon_social"
                  value={form.razon_social}
                  onChange={updateField}
                  disabled={saving}
                  maxLength={200}
                  placeholder="Aircraft Development & Consulting, S.A."
                />
                <div style={ui.help}>
                  The registered name that goes on the invoice.
                </div>
              </div>

              <div style={ui.inputGroup}>
                <Input
                  label="Trade name"
                  name="empresa"
                  value={form.empresa}
                  onChange={updateField}
                  disabled={saving}
                  maxLength={150}
                  placeholder="ADC"
                />
                <div style={ui.help}>
                  How the customer is known day to day.
                </div>
              </div>

              <div style={ui.inputGroup}>
                <Input
                  label="VAT/Tax ID"
                  name="vat_tax_id"
                  value={form.vat_tax_id}
                  onChange={updateField}
                  disabled={saving}
                  maxLength={50}
                  placeholder="1234567-8"
                />
                <div style={ui.help}>NIT, VAT, EIN or the equivalent.</div>
              </div>

              <div style={ui.ancho}>
                <Input
                  label="Billing email"
                  type="email"
                  name="correo_facturacion"
                  value={form.correo_facturacion}
                  onChange={updateField}
                  disabled={saving}
                  maxLength={120}
                  placeholder={form.correo || "billing@company.com"}
                />
                <div style={ui.help}>
                  Only when invoices go somewhere other than the account
                  email.
                </div>
              </div>
            </>
          )}

          <div style={{ ...ui.footer, ...ui.ancho }}>
            <button type="button" onClick={onClose} style={ui.cancelBtn} disabled={saving}>
              Cancel
            </button>

            <button
              type="submit"
              style={{ ...ui.saveBtn, ...(saving ? ui.saveBtnDisabled : null) }}
              disabled={saving}
            >
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Input({ label, ...props }) {
  return (
    <div style={ui.inputGroup}>
      <label style={ui.label}>{label}</label>
      <input {...props} style={ui.input} />
    </div>
  );
}

const ui = {
  /* Las dos tarjetas del tipo de cuenta. */
  tipoRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
  },

  tipoCard: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    border: "1px solid #dbe2ea",
    borderRadius: 10,
    background: "#ffffff",
    cursor: "pointer",
  },

  /* `border` entero y no solo `borderColor`: React avisa cuando en un
     mismo elemento conviven la forma corta y la larga, porque el orden en
     que se aplican no esta garantizado. */
  tipoCardOn: {
    border: "1px solid #0f5e9c",
    background: "#f2f8fb",
  },

  tipoRadio: {
    width: 16,
    height: 16,
    flex: "none",
    margin: 0,
    accentColor: "#0f5e9c",
    cursor: "pointer",
  },

  tipoTitulo: {
    display: "block",
    fontSize: 14,
    fontWeight: 600,
    color: "#0b1f33",
  },

  tipoAyuda: {
    display: "block",
    fontSize: 12,
    color: "#6b7c8f",
  },

  seccion: {
    marginTop: 4,
    paddingTop: 14,
    borderTop: "1px solid #eef2f6",
    fontSize: 13,
    fontWeight: 700,
    color: "#0b1f33",
  },

  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
    zIndex: 2000,
  },
  /* Era de 450px de ancho, con `overflow: hidden` y sin alto maximo: al
     elegir "Company" aparecian doce campos uno debajo del otro y el boton
     de guardar se salia de la pantalla, recortado, sin forma de llegar a
     el. Ahora el alto lo limita la ventana y lo que se mueve es el
     formulario, no el modal entero. */
  modal: {
    width: "min(780px, 96vw)",
    maxHeight: "92vh",
    background: "#ffffff",
    borderRadius: "14px",
    boxShadow: "0 6px 22px rgba(0,0,0,0.15)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    padding: "16px 22px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#1f2937",
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: "22px",
    cursor: "pointer",
    color: "#6b7280",
    opacity: 0.9,
  },
  alert: {
    margin: "14px 22px 0",
    padding: "10px 12px",
    borderRadius: "10px",
    border: "1px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    fontSize: "13px",
    fontWeight: 600,
  },
  alertError: { background: "#FEF2F2", borderColor: "#FECACA", color: "#7F1D1D" },
  alertInfo: { background: "#EFF6FF", borderColor: "#BFDBFE", color: "#1E3A8A" },
  alertX: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: "16px",
    opacity: 0.7,
  },
  /* Dos columnas: con una sola, los campos de empresa estiraban el modal
     al doble de alto y obligaban a bajar para ver el ultimo. */
  form: {
    padding: "22px",
    overflowY: "auto",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    columnGap: "14px",
    rowGap: "14px",
    alignContent: "start",
  },

  /* Lo que ocupa el renglon entero: el tipo de cuenta, el correo, el
     titulo de la seccion y los botones. */
  ancho: { gridColumn: "1 / -1" },

  inputGroup: { marginBottom: 0 },
  label: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#374151",
    marginBottom: "6px",
    display: "block",
  },
  input: {
    width: "100%",
    padding: "11px 12px",
    borderRadius: "8px",
    border: "1px solid #d1d5db",
    fontSize: "15px",
    background: "white",
    outline: "none",
    boxSizing: "border-box",
  },
  select: {
    width: "100%",
    padding: "11px 12px",
    borderRadius: "8px",
    border: "1px solid #d1d5db",
    fontSize: "15px",
    background: "white",
    outline: "none",
    boxSizing: "border-box",
  },
  row: { display: "flex", gap: "12px", alignItems: "flex-start" },
  colCode: { flex: "0 0 260px", minWidth: "260px" },
  colPhone: { flex: 1, minWidth: 0 },

  select: {
  width: "100%",
  padding: "11px 12px",
  borderRadius: "8px",
  border: "1px solid #d1d5db",
  fontSize: "15px",
  background: "white",
  outline: "none",
  boxSizing: "border-box",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
},
  /* El -10px de antes compensaba el margen inferior que tenia cada campo.
     Con la grilla ese margen lo pone el rowGap, asi que el negativo subia
     la ayuda encima del borde del campo de abajo. */
  help: {
    marginTop: "5px",
    fontSize: "12px",
    lineHeight: 1.35,
    color: "#64748b",
    fontWeight: 500,
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    paddingTop: "14px",
    borderTop: "1px solid #e5e7eb",
  },
  cancelBtn: {
    padding: "10px 18px",
    background: "#e5e7eb",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    fontWeight: 600,
  },
  saveBtn: {
    padding: "10px 18px",
    background: "#2563eb",
    color: "white",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    fontWeight: 700,
  },
  saveBtnDisabled: { opacity: 0.75, cursor: "not-allowed" },
};

export default CustomerFormModal;
